/**
 * Speaking module state hook.
 *
 * Integrates speaking module with backend data, context audio playback,
 * microphone recording, and submission.
 *
 * @module useSpeakingModuleState
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useModuleData } from './useModuleData';
import { useSimulationDraft } from './useSimulationDraft';
import { generateSessionId } from '@/utils/sessionUtils';
import { transformSpeakingData } from '@/utils/speakingDataTransform';
import { reorderSimulationData } from '@/utils/reorderSimulationData';
import { readDraftQuestionOrder } from '@/lib/simulationDraftStorage';
import { buildMediaSource } from '@/utils/examSimulation';
import { fetchSpeakingModuleData, saveSimulationResult, saveUserAnswersBatch } from '@/lib/examApi';
import { logger } from '@/lib/logger';
import type { SpeakingQuestion } from '@/types/exam';
import type { CEFRLevel, ExamType, SimulationSettings, UserAnswerSubmission, SimulationResultSubmission } from '@/types/exam-simulation';

type SpeakingMode = 'mic-setup' | 'section-audio' | 'context-audio' | 'prep' | 'recording' | 'processing' | 'done' | 'live-conversation';
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'retrying';

/**
 * Entry mode once any section/context audio has finished: a conversation task
 * routes to the live-conversation flow (LCT-001) instead of `prep → recording`.
 * Non-conversation questions are unchanged.
 */
function nonAudioEntryMode(question: SpeakingQuestion | null): SpeakingMode {
  return question?.isLiveConversation ? 'live-conversation' : 'prep';
}

interface RecordingPayload {
  base64: string;
  format: string;
  durationSeconds: number;
}

interface SpeakingState {
  currentQuestionIndex: number;
  mode: SpeakingMode;
  prepTimeLeft: number;
  recordTimeLeft: number;
  /** Actual elapsed recording duration in whole seconds, set when recording stops */
  recordedDurationSeconds: number;
  isFlashing: boolean;
  permissionStatus: 'unknown' | 'granted' | 'denied';
  contextAudioStatus: 'idle' | 'playing' | 'error';
  sectionAudioStatus: 'idle' | 'playing' | 'error';
  isSaving: boolean;
  saveStatus: SaveStatus;
  saveError: string | null;
  retryCount: number;
  /** Live mic stream, exposed so the recording waveform can analyse real audio */
  micStream: MediaStream | null;
  /** Currently-playing section/context audio element, for the playback waveform */
  activeAudioElement: HTMLAudioElement | null;
}

interface UseSpeakingModuleStateOptions {
  examType: ExamType;
  level: CEFRLevel;
  settings: SimulationSettings | null;
  userId?: string | null;
  onModuleFinish?: () => void;
  /** Session ID passed from parent (if provided, skips local generation) */
  sessionId?: string | null;
  /** When true, all recording actions are blocked (user denied voice consent) */
  voiceConsentDenied?: boolean;
}

export function useSpeakingModuleState({
  examType,
  level,
  settings,
  userId,
  onModuleFinish,
  sessionId: externalSessionId,
  voiceConsentDenied = false,
}: UseSpeakingModuleStateOptions) {
  const [state, setState] = useState<SpeakingState>({
    currentQuestionIndex: 0,
    // Enter mic-setup mode for first-time users unless voice consent is denied
    // (draft rehydration may override this via onRehydrate below)
    mode: voiceConsentDenied ? 'context-audio' : 'mic-setup',
    prepTimeLeft: 0,
    recordTimeLeft: 0,
    recordedDurationSeconds: 0,
    isFlashing: false,
    permissionStatus: 'unknown',
    contextAudioStatus: 'idle',
    sectionAudioStatus: 'idle',
    isSaving: false,
    saveStatus: 'idle',
    saveError: null,
    retryCount: 0,
    micStream: null,
    activeAudioElement: null,
  });

  // True once the Permissions API confirms mic access is already granted, so the
  // mic-setup screen can be skipped for returning users.
  const [skipMicSetupGranted, setSkipMicSetupGranted] = useState(false);
  const [recordedIdsRevision, setRecordedIdsRevision] = useState(0);

  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingStartRef = useRef<number | null>(null);
  const recordingsRef = useRef<Record<string, RecordingPayload>>({});
  const contextAudioRef = useRef<HTMLAudioElement | null>(null);
  const sectionAudioRef = useRef<HTMLAudioElement | null>(null);
  // Use external session ID if provided, otherwise generate locally (for backward compatibility)
  const sessionIdRef = useRef<string | null>(externalSessionId || null);
  const prevSectionRef = useRef<number | null>(null);
  const moduleFinishedRef = useRef<boolean>(false); // Guard to prevent multiple onModuleFinish calls
  const speakingModeRef = useRef(state.mode);
  speakingModeRef.current = state.mode;

  if (!sessionIdRef.current && userId) {
    sessionIdRef.current = generateSessionId(userId);
  }

  // Resume: pin the exact question set saved in the draft so the learner sees
  // the same questions (and count) they were answering, instead of a freshly
  // re-randomized selection. Read once on mount; fresh runs have no draft and
  // fall through to the normal selection (pins undefined).
  const [pinnedQuestionIds] = useState<string[] | undefined>(() =>
    readDraftQuestionOrder(userId, sessionIdRef.current, 'speaking'),
  );

  const { data: simulationData, isLoading: isLoadingData, error: dataError } = useModuleData({
    module: 'speaking',
    examType,
    level,
    settings,
    userId,
    pinnedQuestionIds,
    fetchFn: fetchSpeakingModuleData,
  });

  // Ordered question UUIDs from the original session; set from onRehydrate.
  // Empty = fresh session (no reordering applied).
  const [restoredQuestionOrder, setRestoredQuestionOrder] = useState<string[]>([]);

  // On resume, reorder question groups to match the stored order so that the
  // sequential displayIds 1, 2, 3… (and therefore each answer's display_order)
  // map to the same questions the learner saw before the interruption,
  // regardless of the backend's per-request shuffle.
  const { questions, sectionAudioMap } = useMemo(() => {
    if (!simulationData) {
      return { questions: [] as SpeakingQuestion[], sectionAudioMap: {} as Record<number, { url?: string | null; binary?: string | null; mimeType?: string | null }> };
    }
    const data =
      restoredQuestionOrder.length > 0
        ? reorderSimulationData(simulationData, restoredQuestionOrder)
        : simulationData;
    return transformSpeakingData(data);
  }, [simulationData, restoredQuestionOrder]);

  const currentQuestion = questions[state.currentQuestionIndex] || null;
  const totalQuestions = questions.length;

  /**
   * Draft persistence for Speaking.
   *
   * Speaking answers are audio blobs (typically hundreds of KB each) which we
   * deliberately do NOT persist to `localStorage` - the size cap would be hit
   * almost immediately and recovery of half a recorded answer is ambiguous at
   * best. Instead we persist just enough metadata (current question index and
   * the list of question IDs that already have an in-memory recording) so
   * that a learner who refreshes mid-module is returned to the right slot.
   */
  const recordedIdsForDraft = useMemo(() => {
    return Object.keys(recordingsRef.current);
  }, [state.currentQuestionIndex, recordedIdsRevision]);

  const speakingDraftPayload = useMemo(
    () => ({
      currentQuestionIndex: state.currentQuestionIndex,
      recordedQuestionIds: recordedIdsForDraft,
      // Stable ordering: persisted so resume can restore the exact question
      // order even when the backend reshuffles groups on the next fetch. The
      // displayId (hence each answer's display_order) is then re-derived in the
      // same order the learner originally saw.
      questionOrder: questions.map((q) => q.id),
    }),
    [state.currentQuestionIndex, recordedIdsForDraft, questions],
  );

  const hasUnsavedSpeakingChanges = state.currentQuestionIndex > 0 || recordedIdsForDraft.length > 0;

  const speakingDraft = useSimulationDraft({
    userId,
    sessionId: sessionIdRef.current,
    module: 'speaking',
    step: 'module',
    examType,
    level,
    timer: null,
    payload: speakingDraftPayload,
    shouldPersist: () => hasUnsavedSpeakingChanges && !moduleFinishedRef.current,
    onRehydrate: (envelope) => {
      const payload = envelope.payload as {
        currentQuestionIndex?: number;
        questionOrder?: string[];
      };
      if (Array.isArray(payload.questionOrder) && payload.questionOrder.length > 0) {
        setRestoredQuestionOrder(payload.questionOrder);
      }
      if (typeof payload.currentQuestionIndex === 'number' && payload.currentQuestionIndex > 0) {
        setState((prev) => ({
          ...prev,
          currentQuestionIndex: payload.currentQuestionIndex ?? 0,
        }));
      }
    },
  });

  /**
   * Detects the best supported audio format for recording.
   * Prioritizes more efficient formats (OGG/Opus) over WebM/Opus.
   * 
   * @returns The best supported audio MIME type
   */
  const getBestAudioFormat = useCallback((): string => {
    if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) {
      return 'audio/webm;codecs=opus'; // Fallback if MediaRecorder not available
    }

    const formats = [
      'audio/ogg;codecs=opus',  // Most efficient (10-20% smaller than WebM)
      'audio/webm;codecs=opus', // Current default
      'audio/webm',             // Fallback without codec spec
    ];

    for (const format of formats) {
      if (MediaRecorder.isTypeSupported(format)) {
        logger.debug(`Using audio format: ${format}`);
        return format;
      }
    }

    // Ultimate fallback
    logger.warn('No preferred audio format supported, using default');
    return 'audio/webm;codecs=opus';
  }, []);

  const requestMicrophone = useCallback(async () => {
    if (mediaStreamRef.current?.active) {
      setState((prev) =>
        prev.micStream === mediaStreamRef.current && prev.permissionStatus === 'granted'
          ? prev
          : {
              ...prev,
              permissionStatus: 'granted',
              micStream: mediaStreamRef.current,
            },
      );
      return;
    }

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setState((prev) => ({ ...prev, permissionStatus: 'denied' }));
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      setState((prev) => ({ ...prev, permissionStatus: 'granted', micStream: stream }));
    } catch (error) {
      logger.error('Microphone permission denied or error', error);
      setState((prev) => ({ ...prev, permissionStatus: 'denied' }));
    }
  }, []);

  useEffect(() => {
    return () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (contextAudioRef.current) {
        contextAudioRef.current.pause();
        contextAudioRef.current.src = '';
      }
      moduleFinishedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (state.mode !== 'mic-setup') {
      void requestMicrophone();
    }
  }, [state.mode, requestMicrophone]);

  /**
   * Returning users who already granted mic permission skip the mic-setup screen.
   * The Permissions API reports the current state without prompting; we record it
   * once on mount and act on it below (deferred until the first question loads).
   */
  useEffect(() => {
    if (voiceConsentDenied) return;
    if (typeof navigator === 'undefined' || !navigator.permissions) return;
    let cancelled = false;
    navigator.permissions
      .query({ name: 'microphone' as PermissionName })
      .then((result) => {
        if (!cancelled && result.state === 'granted') setSkipMicSetupGranted(true);
      })
      .catch(() => {
        /* Permissions API unsupported/blocked — stay in mic-setup. */
      });
    return () => {
      cancelled = true;
    };
  }, [voiceConsentDenied]);

  /**
   * Perform the skip once we know permission is granted AND the first question is
   * loaded. We mirror completeMicSetup's derivation here (rather than calling it,
   * which is declared later) so the audio status flags are set — without them the
   * waveform's "playing" check fails and the visualiser stays flat.
   */
  useEffect(() => {
    if (!skipMicSetupGranted || state.mode !== 'mic-setup' || !currentQuestion) return;

    requestMicrophone();

    const section = currentQuestion.section || 0;
    const isNewSection = prevSectionRef.current !== section;
    const hasSectionAudio =
      isNewSection &&
      sectionAudioMap[section] &&
      (sectionAudioMap[section].url || sectionAudioMap[section].binary);
    const hasContextAudio =
      currentQuestion.contextAudio &&
      (currentQuestion.contextAudio.url || currentQuestion.contextAudio.binary);

    let nextMode: SpeakingMode = nonAudioEntryMode(currentQuestion);
    if (hasSectionAudio) {
      nextMode = 'section-audio';
    } else if (hasContextAudio) {
      nextMode = 'context-audio';
    }

    setState((prev) =>
      prev.mode === 'mic-setup'
        ? {
            ...prev,
            permissionStatus: 'granted',
            mode: nextMode,
            contextAudioStatus: hasContextAudio && !hasSectionAudio ? 'playing' : 'idle',
            sectionAudioStatus: hasSectionAudio ? 'playing' : 'idle',
          }
        : prev,
    );
  }, [skipMicSetupGranted, state.mode, currentQuestion, sectionAudioMap, requestMicrophone]);

  // Reset question state and play section/context audio if available
  useEffect(() => {
    if (!currentQuestion) {
      return;
    }

    // Don't override the mic-setup screen while it's active — the mode will
    // be advanced by completeMicSetup() once the user dismisses the screen.
    if (speakingModeRef.current === 'mic-setup') {
      // Still capture prep/record times so completeMicSetup has fresh values.
      setState((prev) => ({
        ...prev,
        prepTimeLeft: currentQuestion.prepTime ?? 15,
        recordTimeLeft: currentQuestion.speakTime ?? 120,
      }));
      return;
    }

    const prepTime = currentQuestion.prepTime ?? 15;
    const recordTime = currentQuestion.speakTime ?? 120;
    const section = currentQuestion.section || 0;
    const isNewSection = prevSectionRef.current !== section;
    const hasSectionAudio = isNewSection && sectionAudioMap[section] && (sectionAudioMap[section].url || sectionAudioMap[section].binary);
    const hasContextAudio = currentQuestion.contextAudio && (currentQuestion.contextAudio.url || currentQuestion.contextAudio.binary);

    let nextMode: SpeakingMode = nonAudioEntryMode(currentQuestion);
    if (hasSectionAudio) {
      nextMode = 'section-audio';
    } else if (hasContextAudio) {
      nextMode = 'context-audio';
    }

    setState((prev) => ({
      ...prev,
      mode: nextMode,
      prepTimeLeft: prepTime,
      recordTimeLeft: recordTime,
      isFlashing: false,
      contextAudioStatus: hasContextAudio && !hasSectionAudio ? 'playing' : 'idle',
      sectionAudioStatus: hasSectionAudio ? 'playing' : 'idle',
    }));

    prevSectionRef.current = section;

    if (contextAudioRef.current) {
      contextAudioRef.current.pause();
      contextAudioRef.current.src = '';
    }
    if (sectionAudioRef.current) {
      sectionAudioRef.current.pause();
      sectionAudioRef.current.src = '';
    }
  }, [currentQuestion, sectionAudioMap]);

  // Section audio playback
  useEffect(() => {
    if (state.mode !== 'section-audio' || !currentQuestion) {
      return;
    }

    const section = currentQuestion.section || 0;
    const sectionAudio = sectionAudioMap[section];
    if (!sectionAudio) {
      setState((prev) => ({
        ...prev,
        mode: currentQuestion.contextAudio ? 'context-audio' : nonAudioEntryMode(currentQuestion),
        sectionAudioStatus: 'idle',
        contextAudioStatus: currentQuestion.contextAudio ? 'playing' : 'idle',
      }));
      return;
    }

    const src = buildMediaSource(
      sectionAudio.url || null,
      sectionAudio.binary || null,
      sectionAudio.mimeType || undefined,
      'audio/mpeg',
    );

    if (!src) {
      setState((prev) => ({
        ...prev,
        mode: currentQuestion.contextAudio ? 'context-audio' : nonAudioEntryMode(currentQuestion),
        sectionAudioStatus: 'error',
        contextAudioStatus: currentQuestion.contextAudio ? 'playing' : 'idle',
      }));
      return;
    }

    const audio = new Audio(src);
    sectionAudioRef.current = audio;
    audio.preload = 'auto';
    audio.volume = 1;
    setState((prev) => ({ ...prev, activeAudioElement: audio }));

    const handleEnded = () => {
      setState((prev) => ({
        ...prev,
        mode: currentQuestion.contextAudio ? 'context-audio' : nonAudioEntryMode(currentQuestion),
        sectionAudioStatus: 'idle',
        contextAudioStatus: currentQuestion.contextAudio ? 'playing' : 'idle',
      }));
    };

    const handleError = (event: Event) => {
      const error = event.target as HTMLAudioElement;
      logger.error('Section audio failed to play', error.error);
      setState((prev) => ({
        ...prev,
        mode: currentQuestion.contextAudio ? 'context-audio' : nonAudioEntryMode(currentQuestion),
        sectionAudioStatus: 'error',
        contextAudioStatus: currentQuestion.contextAudio ? 'playing' : 'idle',
      }));
    };

    // Tie the status flag to actual playback so the waveform animates
    // regardless of how the mode was entered (e.g. when mic-setup is skipped).
    const handlePlaying = () => {
      setState((prev) => (prev.sectionAudioStatus === 'playing' ? prev : { ...prev, sectionAudioStatus: 'playing' }));
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('playing', handlePlaying);

    audio.play().catch((error) => {
      logger.error('Failed to start section audio', error);
      setState((prev) => ({
        ...prev,
        mode: currentQuestion.contextAudio ? 'context-audio' : nonAudioEntryMode(currentQuestion),
        sectionAudioStatus: 'error',
        contextAudioStatus: currentQuestion.contextAudio ? 'playing' : 'idle',
      }));
    });

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('playing', handlePlaying);
      audio.pause();
      audio.src = '';
      setState((prev) => (prev.activeAudioElement === audio ? { ...prev, activeAudioElement: null } : prev));
    };
  }, [state.mode, currentQuestion, sectionAudioMap]);

  // Context audio playback
  useEffect(() => {
    if (state.mode !== 'context-audio' || !currentQuestion?.contextAudio) {
      return;
    }

    const src = buildMediaSource(
      currentQuestion.contextAudio.url || null,
      currentQuestion.contextAudio.binary || null,
      currentQuestion.contextAudio.mimeType || undefined,
      'audio/mpeg',
    );

    if (!src) {
      setState((prev) => ({
        ...prev,
        mode: nonAudioEntryMode(currentQuestion),
        contextAudioStatus: 'error',
      }));
      return;
    }

    const audio = new Audio(src);
    contextAudioRef.current = audio;
    audio.preload = 'auto';
    audio.volume = 1;
    setState((prev) => ({ ...prev, activeAudioElement: audio }));

    const handleEnded = () => {
      setState((prev) => ({
        ...prev,
        mode: nonAudioEntryMode(currentQuestion),
        contextAudioStatus: 'idle',
      }));
    };

    const handleError = (event: Event) => {
      const error = event.target as HTMLAudioElement;
      logger.error('Context audio failed to play', error.error);
      setState((prev) => ({
        ...prev,
        mode: nonAudioEntryMode(currentQuestion),
        contextAudioStatus: 'error',
      }));
    };

    // Tie the status flag to actual playback so the waveform animates
    // regardless of how the mode was entered (e.g. when mic-setup is skipped).
    const handlePlaying = () => {
      setState((prev) => (prev.contextAudioStatus === 'playing' ? prev : { ...prev, contextAudioStatus: 'playing' }));
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('playing', handlePlaying);

    audio.play().catch((error) => {
      logger.error('Failed to start context audio', error);
      setState((prev) => ({
        ...prev,
        mode: nonAudioEntryMode(currentQuestion),
        contextAudioStatus: 'error',
      }));
    });

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('playing', handlePlaying);
      audio.pause();
      audio.src = '';
      setState((prev) => (prev.activeAudioElement === audio ? { ...prev, activeAudioElement: null } : prev));
    };
  }, [state.mode, currentQuestion]);

  const blobToBase64 = useCallback((blob: Blob) => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          const base64 = result.split(',')[1];
          resolve(base64);
        } else {
          reject(new Error('Failed to convert blob to base64'));
        }
      };
      reader.onerror = () => reject(reader.error || new Error('Failed to read blob'));
      reader.readAsDataURL(blob);
    });
  }, []);

  /**
   * Validates individual recording data.
   */
  const validateRecordingData = useCallback(
    (recording: RecordingPayload, displayId: string | number): string | null => {
      if (!recording.base64) {
        return null;
      }

      // Basic base64 validation - check if it's valid base64
      try {
        // Remove data URL prefix if present
        const base64Data = recording.base64.includes(',')
          ? recording.base64.split(',')[1]
          : recording.base64;

        // Check if it's valid base64
        if (!/^[A-Za-z0-9+/]*={0,2}$/.test(base64Data)) {
          return `Invalid audio data for question ${displayId}`;
        }
      } catch (error) {
        logger.warn('Error validating base64 data', error);
        return `Invalid audio data for question ${displayId}`;
      }

      // Validate format
      const allowedFormats = [
        'audio/ogg',
        'audio/ogg;codecs=opus',
        'audio/webm',
        'audio/webm;codecs=opus',
        'audio/mpeg',
        'audio/mp3',
      ];
      if (
        recording.format &&
        !allowedFormats.some((f) => recording.format?.includes(f))
      ) {
        return `Unsupported audio format: ${recording.format}`;
      }

      return null;
    },
    [],
  );

  /**
   * Validates that all required data is present before saving.
   * Returns validation error message if validation fails, null if valid.
   */
  const validateRecordings = useCallback((): string | null => {
    if (!userId) {
      return 'User ID is required';
    }
    if (!sessionIdRef.current) {
      return 'Session ID is required';
    }
    if (!questions.length) {
      return 'No questions to save';
    }

    // Validate that all questions are accounted for
    for (const question of questions) {
      const recording = recordingsRef.current[question.id];

      // If recording exists, validate data
      if (recording) {
        const error = validateRecordingData(
          recording,
          question.displayId || question.id,
        );
        if (error) {
          return error;
        }
      }
    }

    return null;
  }, [questions, userId, validateRecordingData]);

  const prepareAnswerSubmissions = useCallback((): UserAnswerSubmission[] => {
    const sessionId = sessionIdRef.current;
    if (!userId || !sessionId) {
      return [];
    }

    return questions
      // Conversation tasks are persisted server-side by the realtime bridge
      // (combined audio + labeled transcript); excluding them here avoids a
      // duplicate empty answer while leaving every other question unchanged.
      .filter((question) => !question.isLiveConversation)
      .map((question) => {
      const recording = recordingsRef.current[question.id];
      return {
        user_id: userId,
        session_id: sessionId,
        question_id: question.id,
        module: 'speaking',
        section: question.section || 1,
        answer_text: null,
        audio_data: recording?.base64 ?? null,
        audio_format: recording?.format ?? null,
        audio_duration_seconds: recording?.durationSeconds ?? null,
        is_correct: null,
        points: null,
        // 1..N position the learner saw this question in during the (possibly
        // shuffled) exam, so the results/review page can replay the same order
        // instead of falling back to the authored question order.
        display_order: question.displayId,
      };
    });
  }, [questions, userId]);

  const prepareResultSubmission = useCallback((): SimulationResultSubmission | null => {
    const sessionId = sessionIdRef.current;
    if (!userId || !sessionId) {
      return null;
    }

    const recordedCount = Object.keys(recordingsRef.current).length;

    return {
      session_id: sessionId,
      user_id: userId,
      is_success: recordedCount > 0,
      passed: null,
      score: null,
      notes: null,
      level,
      is_graded: false,
      module: 'speaking',
      exam_type: examType,
    };
  }, [userId, level, examType]);

  /**
   * Determines if an error is retryable (network errors, timeouts).
   * Validation errors are not retryable.
   */
  const isRetryableError = useCallback((error: unknown): boolean => {
    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      // Network errors, timeouts, and server errors (5xx) are retryable
      return (
        message.includes('network') ||
        message.includes('timeout') ||
        message.includes('fetch') ||
        message.includes('connection') ||
        message.includes('500') ||
        message.includes('502') ||
        message.includes('503') ||
        message.includes('504')
      );
    }
    // If it's a response with status, check if it's a server error
    if (typeof error === 'object' && error !== null && 'status' in error) {
      const status = (error as { status: number }).status;
      return status >= 500 && status < 600;
    }
    return false;
  }, []);

  /**
   * Sleep utility for exponential backoff retry delays.
   */
  const sleep = useCallback((ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }, []);

  /**
   * Attempts to save recordings with retry logic.
   * 
   * IMPORTANT: Saves answers first, then saves the simulation result.
   * This order is critical because the backend triggers AI analysis automatically
   * when a simulation result is saved with module="speaking", and the analysis
   * requires all answers to be saved first.
   * 
   * Flow: Save answers → Save result → Backend enqueues durable AI analysis
   * 
   * Returns true if save succeeded, false otherwise.
   */
  const attemptSave = useCallback(async (answers: UserAnswerSubmission[], result: SimulationResultSubmission | null, retryAttempt: number = 0): Promise<boolean> => {
    const MAX_RETRIES = 3;
    const backoffDelays = [1000, 2000, 4000]; // 1s, 2s, 4s

    try {
      // Step 1: Save all recordings first
      if (answers.length > 0) {
        logger.debug(`Saving ${answers.length} speaking answers before result`, {
          event: 'simulation.speaking.answers.save.started',
          sessionId: result?.session_id,
          retryAttempt,
        });
        await saveUserAnswersBatch({ answers });
        logger.debug('Speaking answers saved successfully');
      }

      // Step 2: Save simulation result (this enqueues AI analysis in the backend for speaking module)
      if (result) {
        logger.debug('Saving simulation result (will enqueue AI analysis)', {
          event: 'simulation.speaking.result.save.started',
          sessionId: result.session_id,
          module: result.module,
          level: result.level,
        });
        await saveSimulationResult(result);
        logger.debug('Simulation result saved, AI analysis job enqueued');
      }

      return true;
    } catch (error) {
      logger.error(`Failed to save speaking module (attempt ${retryAttempt + 1}/${MAX_RETRIES + 1})`, error);
      logger.event(
        'simulation.speaking.save.retry_or_fail',
        {
          sessionId: result?.session_id,
          retryAttempt,
          answerCount: answers.length,
        },
        'error',
        'Speaking module save failed',
      );

      // Check if error is retryable and we haven't exceeded max retries
      if (isRetryableError(error) && retryAttempt < MAX_RETRIES) {
        const delay = backoffDelays[retryAttempt] || 4000;

        setState((prev) => ({
          ...prev,
          saveStatus: 'retrying',
          retryCount: retryAttempt + 1,
          saveError: `Saving failed. Retrying... (attempt ${retryAttempt + 2}/${MAX_RETRIES + 1})`,
        }));

        await sleep(delay);

        return attemptSave(answers, result, retryAttempt + 1);
      }

      // Non-retryable error or max retries exceeded
      const errorMessage = error instanceof Error
        ? error.message
        : 'Failed to save recordings. Please try again or contact support.';

      setState((prev) => ({
        ...prev,
        saveStatus: 'error',
        saveError: errorMessage,
      }));

      return false;
    }
  }, [isRetryableError, sleep]);

  const handleFinishModule = useCallback(async () => {
    // Guard: Prevent multiple calls to handleFinishModule
    if (moduleFinishedRef.current) {
      logger.debug('Module already finished, ignoring duplicate call');
      return;
    }

    if (state.isSaving || state.saveStatus === 'saving' || state.saveStatus === 'retrying' || !questions.length) {
      return;
    }

    if (!userId || !sessionIdRef.current) {
      logger.warn('Cannot save speaking module without user/session');
      setState((prev) => ({
        ...prev,
        saveStatus: 'error',
        saveError: 'Cannot save: missing user or session information',
      }));
      return;
    }

    // Validate recordings before attempting save
    const validationError = validateRecordings();
    if (validationError) {
      logger.error('Validation failed before save', validationError);
      logger.event(
        'simulation.speaking.validation_failed',
        {
          sessionId: sessionIdRef.current,
          questionIndex: state.currentQuestionIndex,
          questionId: currentQuestion?.id,
          validationError,
        },
        'warn',
        'Speaking module validation failed',
      );
      setState((prev) => ({
        ...prev,
        saveStatus: 'error',
        saveError: validationError,
      }));
      return;
    }

    try {
      setState((prev) => ({
        ...prev,
        isSaving: true,
        saveStatus: 'saving',
        saveError: null,
        retryCount: 0,
      }));

      const answers = prepareAnswerSubmissions();
      const result = prepareResultSubmission();
      logger.event(
        'simulation.speaking.finish.started',
        {
          sessionId: sessionIdRef.current,
          questionCount: questions.length,
          answerCount: answers.length,
        },
        'info',
        'Speaking module finish started',
      );

      const success = await attemptSave(answers, result);

      if (success) {
        setState((prev) => ({
          ...prev,
          saveStatus: 'saved',
          saveError: null,
        }));

        // Mark module as finished to prevent duplicate calls
        moduleFinishedRef.current = true;
        speakingDraft.clear();

        // Show success message briefly before completing
        await sleep(2000);

        // Call onModuleFinish only once (check guard again after sleep)
        if (onModuleFinish) {
          logger.debug('Calling onModuleFinish callback');
          logger.event(
            'simulation.speaking.finish.completed',
            {
              sessionId: sessionIdRef.current,
              answerCount: answers.length,
            },
            'info',
            'Speaking module finish completed',
          );
          onModuleFinish();
        }
      }
      // If save failed, error state is already set by attemptSave
    } catch (error) {
      logger.error('Unexpected error in handleFinishModule', error);
      setState((prev) => ({
        ...prev,
        saveStatus: 'error',
        saveError: 'An unexpected error occurred. Please try again.',
      }));
    } finally {
      setState((prev) => ({ ...prev, isSaving: false }));
    }
  }, [
    prepareAnswerSubmissions,
    prepareResultSubmission,
    validateRecordings,
    attemptSave,
    onModuleFinish,
    questions.length,
    state.isSaving,
    state.saveStatus,
    userId,
    sleep,
    speakingDraft,
  ]);

  /**
   * Manual retry handler for user-initiated retry after error.
   */
  const handleRetrySave = useCallback(async () => {
    if (!userId || !sessionIdRef.current || !questions.length) {
      return;
    }

    const validationError = validateRecordings();
    if (validationError) {
      setState((prev) => ({
        ...prev,
        saveStatus: 'error',
        saveError: validationError,
      }));
      return;
    }

    const answers = prepareAnswerSubmissions();
    const result = prepareResultSubmission();

    setState((prev) => ({
      ...prev,
      saveStatus: 'retrying',
      saveError: null,
      retryCount: 0,
      isSaving: true,
    }));

    const success = await attemptSave(answers, result, 0);

    if (success) {
      setState((prev) => ({
        ...prev,
        saveStatus: 'saved',
        saveError: null,
      }));

      speakingDraft.clear();

      await sleep(2000);

      if (onModuleFinish) {
        onModuleFinish();
      }
    }

    setState((prev) => ({ ...prev, isSaving: false }));
  }, [
    prepareAnswerSubmissions,
    prepareResultSubmission,
    validateRecordings,
    attemptSave,
    onModuleFinish,
    questions.length,
    userId,
    sleep,
    speakingDraft,
  ]);

  const handleRecordingStop = useCallback(async () => {
    if (!mediaRecorderRef.current || !currentQuestion) return;

    const blob = new Blob(recordingChunksRef.current, {
      type: mediaRecorderRef.current.mimeType,
    });
    const durationMs = recordingStartRef.current
      ? performance.now() - recordingStartRef.current
      : 0;
    const durationSeconds = Math.max(0, durationMs / 1000);

    try {
      const base64 = await blobToBase64(blob);
      recordingsRef.current[currentQuestion.id] = {
        base64,
        format: mediaRecorderRef.current.mimeType || 'audio/webm',
        durationSeconds,
      };
      setRecordedIdsRevision((revision) => revision + 1);
      speakingDraft.requestSaveIndicator();
    } catch (error) {
      logger.error('Failed to convert recording to base64', error);
    }

    setState((prev) => ({ ...prev, mode: 'done', isFlashing: false, recordedDurationSeconds: Math.round(durationSeconds) }));

    setTimeout(() => {
      setState((prev) => {
        if (prev.currentQuestionIndex < totalQuestions - 1) {
          return {
            ...prev,
            currentQuestionIndex: prev.currentQuestionIndex + 1,
          };
        } else {
          handleFinishModule();
          return prev;
        }
      });
    }, 1200);
  }, [currentQuestion, totalQuestions, handleFinishModule, blobToBase64, speakingDraft]);

  const handleStartRecording = useCallback(async () => {
    if (voiceConsentDenied) return;
    if (!currentQuestion || state.mode === 'recording' || state.permissionStatus === 'denied') {
      if (state.permissionStatus === 'denied') {
        setState((prev) => ({ ...prev, mode: 'done' }));
        handleFinishModule();
      }
      return;
    }

    try {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        setState((prev) => ({ ...prev, permissionStatus: 'denied' }));
        return;
      }

      let stream = mediaStreamRef.current;
      if (!stream) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaStreamRef.current = stream;
        const acquiredStream = stream;
        setState((prev) => ({ ...prev, micStream: acquiredStream }));
      }

      recordingChunksRef.current = [];
      const bestFormat = getBestAudioFormat();
      const recorder = new MediaRecorder(stream, { mimeType: bestFormat });
      mediaRecorderRef.current = recorder;
      recordingStartRef.current = performance.now();

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = handleRecordingStop;
      recorder.start();

      setState((prev) => ({
        ...prev,
        mode: 'recording',
        isFlashing: false,
      }));
    } catch (error) {
      logger.error('Failed to start recording', error);
      setState((prev) => ({ ...prev, permissionStatus: 'denied' }));
    }
  }, [
    voiceConsentDenied,
    blobToBase64,
    currentQuestion,
    state.mode,
    state.permissionStatus,
    totalQuestions,
    state.currentQuestionIndex,
    handleFinishModule,
    getBestAudioFormat,
  ]);

  const handleStopRecording = useCallback(() => {
    if (state.mode !== 'recording') {
      return;
    }

    setState((prev) => ({ ...prev, mode: 'processing' }));
    mediaRecorderRef.current?.stop();
  }, [state.mode]);

  // Prep timer countdown
  useEffect(() => {
    if (!currentQuestion || state.mode !== 'prep' || state.prepTimeLeft <= 0) {
      if (state.mode === 'prep' && state.prepTimeLeft <= 0) {
        handleStartRecording();
      }
      return;
    }

    const timer = setInterval(() => {
      setState((prev) => ({
        ...prev,
        prepTimeLeft: Math.max(0, prev.prepTimeLeft - 1),
        isFlashing: prev.prepTimeLeft === 1 ? true : prev.isFlashing,
      }));
    }, 1000);

    return () => clearInterval(timer);
  }, [state.mode, state.prepTimeLeft, currentQuestion, handleStartRecording]);

  // Recording timer countdown
  useEffect(() => {
    if (!currentQuestion || state.mode !== 'recording' || state.recordTimeLeft <= 0) {
      if (state.mode === 'recording' && state.recordTimeLeft <= 0) {
        handleStopRecording();
      }
      return;
    }

    const timer = setInterval(() => {
      setState((prev) => ({
        ...prev,
        recordTimeLeft: Math.max(0, prev.recordTimeLeft - 1),
      }));
    }, 1000);

    return () => clearInterval(timer);
  }, [state.mode, state.recordTimeLeft, currentQuestion, handleStopRecording]);

  /**
   * Transitions from `mic-setup` to the appropriate first-question mode.
   * Called by MicSetupScreen on both "Continue" and "Skip mic check".
   * The permission status has already been updated in state by the time
   * this is called (either granted or denied/skipped).
   */
  const completeMicSetup = useCallback(() => {
    if (state.mode !== 'mic-setup') return;

    // Re-derive the first-question mode from the current question (same logic
    // as the currentQuestion useEffect that normally sets the initial mode).
    if (!currentQuestion) {
      setState((prev) => ({ ...prev, mode: 'prep' }));
      return;
    }

    const section = currentQuestion.section || 0;
    const isNewSection = prevSectionRef.current !== section;
    const hasSectionAudio =
      isNewSection &&
      sectionAudioMap[section] &&
      (sectionAudioMap[section].url || sectionAudioMap[section].binary);
    const hasContextAudio =
      currentQuestion.contextAudio &&
      (currentQuestion.contextAudio.url || currentQuestion.contextAudio.binary);

    let nextMode: SpeakingMode = nonAudioEntryMode(currentQuestion);
    if (hasSectionAudio) {
      nextMode = 'section-audio';
    } else if (hasContextAudio) {
      nextMode = 'context-audio';
    }

    setState((prev) => ({
      ...prev,
      mode: nextMode,
      contextAudioStatus: hasContextAudio && !hasSectionAudio ? 'playing' : 'idle',
      sectionAudioStatus: hasSectionAudio ? 'playing' : 'idle',
    }));
  }, [state.mode, currentQuestion, sectionAudioMap]);

  /**
   * Called when a live-conversation question finishes (the bridge has already
   * persisted its answer). Advances to the next question or finishes the module,
   * mirroring the post-recording transition without saving a local recording.
   */
  const handleConversationComplete = useCallback(() => {
    setState((prev) => {
      if (prev.currentQuestionIndex < totalQuestions - 1) {
        return { ...prev, currentQuestionIndex: prev.currentQuestionIndex + 1 };
      }
      handleFinishModule();
      return prev;
    });
  }, [totalQuestions, handleFinishModule]);

  const handleStartEarly = useCallback(() => {
    if (!currentQuestion) return;

    if (state.mode === 'context-audio' && contextAudioRef.current) {
      contextAudioRef.current.pause();
      contextAudioRef.current.src = '';
    }
    if (state.mode === 'section-audio' && sectionAudioRef.current) {
      sectionAudioRef.current.pause();
      sectionAudioRef.current.src = '';
    }

    // Skipping the section intro continues to the next natural step rather than
    // jumping straight into recording (which the natural audio-end path never
    // does): play this question's own context audio if it has any, otherwise
    // show the preparation frame — `prep` for ordinary questions, or the
    // live-conversation thinking screen for conversation tasks.
    if (state.mode === 'section-audio') {
      const hasContextAudio =
        currentQuestion.contextAudio &&
        (currentQuestion.contextAudio.url || currentQuestion.contextAudio.binary);
      setState((prev) => ({
        ...prev,
        mode: hasContextAudio ? 'context-audio' : nonAudioEntryMode(currentQuestion),
        sectionAudioStatus: 'idle',
        contextAudioStatus: hasContextAudio ? 'playing' : 'idle',
      }));
      return;
    }

    // Conversation tasks have no prep → recording flow; drop into their own
    // thinking/preparation frame instead of starting a recording.
    if (currentQuestion.isLiveConversation) {
      setState((prev) => ({ ...prev, mode: 'live-conversation', contextAudioStatus: 'idle' }));
      return;
    }

    setState((prev) => ({ ...prev, mode: 'recording', prepTimeLeft: 0 }));
    handleStartRecording();
  }, [handleStartRecording, state.mode, currentQuestion]);

  return {
    questions,
    currentQuestion,
    totalQuestions,
    state,
    sessionId: sessionIdRef.current,
    isLoadingData,
    dataError,
    handleStartRecording,
    handleStopRecording,
    handleStartEarly,
    handleFinishModule,
    handleRetrySave,
    handleConversationComplete,
    completeMicSetup,
    requestMicrophone,
  };
}
